import React, { useState, useEffect } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import ReactPaginate from 'react-paginate';
import '../../assets/css/table-mobile-responsiveness.css';
import '../../assets/css/react-confirmation-style.css';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import axios from 'axios';

const DepartmentManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState({
    id: '',
    name: '',
    type: 'internal',
    prefix: '',
    short_prefix: '',
    logo_url: '',
    description: ''
  });

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch all departments
  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);

      const response = await axios.get(`${API_URL}/api/departments?${params.toString()}`);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to fetch departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [statusFilter, typeFilter]);

  // Open modal for creating new department
  const openCreateModal = () => {
    setSelectedDepartment({
      id: '',
      name: '',
      type: 'internal',
      prefix: '',
      short_prefix: '',
      logo_url: '',
      description: ''
    });
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  // Open modal for editing department
  const openEditModal = (dept) => {
    setSelectedDepartment(dept);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDepartment({
      id: '',
      name: '',
      type: 'internal',
      prefix: '',
      short_prefix: '',
      logo_url: '',
      description: ''
    });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    if (field === 'type') {
      // When type changes to corporate, set short_prefix to 'cr'
      if (value === 'corporate') {
        setSelectedDepartment((prev) => ({
          ...prev,
          [field]: value,
          short_prefix: 'cr'
        }));
      } else {
        setSelectedDepartment((prev) => ({ ...prev, [field]: value }));
      }
    } else {
      setSelectedDepartment((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Create new department
  const handleCreate = async (e) => {
    e.preventDefault();

    if (!selectedDepartment.name.trim()) {
      toast.error('Department name is required');
      return;
    }

    try {
      // Determine prefix and short_prefix based on type
      let prefix, short_prefix;

      if (selectedDepartment.type === 'corporate') {
        // For corporate clients: prefix = department name (lowercase), short_prefix = 'cr'
        prefix = selectedDepartment.prefix || selectedDepartment.name.toLowerCase().replace(/\s+/g, '');
        short_prefix = 'cr';
      } else {
        // For internal departments: use provided values or generate defaults
        prefix = selectedDepartment.prefix || selectedDepartment.name.toLowerCase().replace(/\s+/g, '');
        short_prefix = selectedDepartment.short_prefix || selectedDepartment.name.substring(0, 2).toLowerCase();
      }

      await axios.post(`${API_URL}/api/departments`, {
        name: selectedDepartment.name.trim(),
        type: selectedDepartment.type,
        prefix: prefix,
        short_prefix: short_prefix,
        logo_url: selectedDepartment.logo_url || null,
        description: selectedDepartment.description || null
      });

      toast.success('Department created successfully');
      closeModal();
      fetchDepartments();
    } catch (error) {
      console.error('Error creating department:', error);
      toast.error(error.response?.data?.error || 'Failed to create department');
    }
  };

  // Update existing department
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!selectedDepartment.name.trim()) {
      toast.error('Department name is required');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/departments/${selectedDepartment.id}`, {
        name: selectedDepartment.name.trim(),
        type: selectedDepartment.type,
        prefix: selectedDepartment.prefix,
        short_prefix: selectedDepartment.short_prefix,
        logo_url: selectedDepartment.logo_url,
        description: selectedDepartment.description,
        status: selectedDepartment.status
      });

      toast.success('Department updated successfully');
      closeModal();
      fetchDepartments();
    } catch (error) {
      console.error('Error updating department:', error);
      toast.error(error.response?.data?.error || 'Failed to update department');
    }
  };

  // Delete department (soft delete)
  const handleDelete = (dept) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md mx-4 w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="fas fa-trash text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-blue mb-2">Confirm Deactivation</h2>
              <p className="text-grey">Are you sure you want to deactivate "{dept.name}"?</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-grey font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.delete(`${API_URL}/api/departments/${dept.id}`);
                    toast.success('Department deactivated successfully');
                    fetchDepartments();
                    onClose();
                  } catch (error) {
                    console.error('Error deleting department:', error);
                    toast.error('Failed to deactivate department');
                  }
                }}
                className="flex-1 px-6 py-3 bg-red text-white font-semibold rounded-xl hover:bg-red/90 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )
    });
  };

  // Filter and paginate
  const filteredDepartments = departments.filter((dept) => {
    const matchesSearch = dept.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const paginatedData = filteredDepartments.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const handlePageClick = (data) => setCurrentPage(data.selected);

  const getTypeColor = (type) => {
    return type === 'corporate'
      ? 'bg-purple-100 text-purple-700 border border-purple-300'
      : 'bg-blue/10 text-blue border border-blue/30';
  };

  const getStatusColor = (status) => {
    return status === 'active'
      ? 'bg-green-100 text-green-700 border border-green-300'
      : 'bg-red/10 text-red border border-red/30';
  };

  return (
    <section className="flex flex-col justify-center font-poppins bg-gray-100 relative overflow-auto">
      <div className="relative flex flex-col justify-center py-6 px-4">
        {/* Enhanced Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-3">
            <h1 className="text-4xl font-bold text-blue">Department Management</h1>
          </div>
          <p className="text-grey font-medium">Manage all departments and corporate clients</p>
        </div>

        {/* Modern Form Container */}
        <div className="w-full max-w-7xl mx-auto">
          <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden mb-8">
            {/* Form Header */}
            <div className="bg-blue px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <i className="fas fa-filter text-white text-xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-xl">Search & Filter</h2>
                  <p className="text-white/80 text-sm">Find and filter departments easily</p>
                </div>
              </div>
            </div>

            {/* Form Body */}
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Add Button */}
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-plus text-blue text-xs"></i>
                      Add New
                    </label>
                    <button
                      onClick={openCreateModal}
                      className="w-full bg-blue hover:bg-blue/80 text-white px-6 py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-plus"></i>
                      Add Department
                    </button>
                  </div>
                </div>

                {/* Search Section */}
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-search text-blue text-xs"></i>
                      Search
                    </label>
                    <input
                      type="text"
                      placeholder="Search departments..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md"
                    />
                  </div>
                </div>

                {/* Type Filter */}
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-filter text-blue text-xs"></i>
                      Type
                    </label>
                    <div className="relative">
                      <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value="">All Types</option>
                        <option value="internal">Internal</option>
                        <option value="corporate">Corporate</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Filter */}
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-toggle-on text-blue text-xs"></i>
                      Status
                    </label>
                    <div className="relative">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rows Per Page */}
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-eye text-blue text-xs"></i>
                      Rows per Page
                    </label>
                    <div className="relative">
                      <select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setCurrentPage(0);
                        }}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value={10}>10 rows</option>
                        <option value={20}>20 rows</option>
                        <option value={50}>50 rows</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Table/Loading Section */}
        {loading ? (
          <div className="w-full max-w-7xl mx-auto">
            <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
              <div className="p-12">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue/20 border-t-blue rounded-full animate-spin"></div>
                    <div className="w-16 h-16 border-4 border-transparent border-t-yellow rounded-full animate-spin absolute inset-0 animate-ping"></div>
                  </div>
                  <div className="text-blue font-medium text-lg">Loading departments...</div>
                  <div className="text-grey text-sm">Please wait while we fetch your data</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-7xl mx-auto">
            <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
              {/* Table Header */}
              <div className="bg-blue px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <i className="fas fa-table text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">Department Data</h3>
                    <p className="text-white/80 text-sm">
                      {paginatedData.length} of {filteredDepartments.length} departments
                    </p>
                  </div>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <Table className="w-full">
                  <Thead>
                    <Tr className="bg-gray-50 border-b border-gray-200">
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">Name</div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">Type</div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">Prefix</div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">Short Prefix</div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">Status</div>
                      </Th>
                      <Th className="px-6 py-4 text-center font-semibold text-sm text-blue bg-gray-200 sticky right-0">
                        <div className="flex items-center justify-center gap-2">Actions</div>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((dept, index) => (
                        <Tr key={index} className="hover:bg-blue/5 transition-all duration-200 border-b border-gray-100 group">
                          <Td className="px-6 py-4 font-semibold text-grey">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-blue/20 rounded-full flex items-center justify-center">
                                <i className="fas fa-building text-blue text-xs"></i>
                              </div>
                              {dept.name}
                            </div>
                          </Td>
                          <Td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(dept.type)}`}>
                              <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                              {dept.type}
                            </span>
                          </Td>
                          <Td className="px-6 py-4 font-medium text-grey font-mono text-sm">
                            {dept.prefix || '-'}
                          </Td>
                          <Td className="px-6 py-4 font-medium text-grey font-mono text-sm">
                            {dept.short_prefix || '-'}
                          </Td>
                          <Td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(dept.status)}`}>
                              <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                              {dept.status}
                            </span>
                          </Td>
                          <Td className="px-6 py-4 bg-gray-200 sticky right-0">
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => openEditModal(dept)}
                                className="group bg-blue hover:bg-blue/80 text-white px-3 py-2 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2 text-sm"
                              >
                                <i className="fas fa-edit text-xs group-hover:animate-pulse"></i>
                                Edit
                              </button>
                              {dept.status === 'active' && (
                                <button
                                  onClick={() => handleDelete(dept)}
                                  className="group bg-red hover:bg-red/80 text-white px-3 py-2 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2 text-sm"
                                >
                                  <i className="fas fa-trash text-xs group-hover:animate-pulse"></i>
                                  Deactivate
                                </button>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan="6" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center text-grey">
                            <i className="fas fa-folder-open text-5xl mb-4 opacity-20"></i>
                            <p className="font-medium">No departments found</p>
                            <p className="text-sm mt-1">Try adjusting your filters</p>
                          </div>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredDepartments.length > rowsPerPage && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <p className="text-sm text-grey">
                      Showing {currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, filteredDepartments.length)} of {filteredDepartments.length} departments
                    </p>
                    <ReactPaginate
                      previousLabel={
                        <div className="flex items-center gap-2">
                          <i className="fas fa-chevron-left text-xs"></i>
                          <span className="hidden sm:inline">Previous</span>
                        </div>
                      }
                      nextLabel={
                        <div className="flex items-center gap-2">
                          <span className="hidden sm:inline">Next</span>
                          <i className="fas fa-chevron-right text-xs"></i>
                        </div>
                      }
                      pageCount={Math.ceil(filteredDepartments.length / rowsPerPage)}
                      onPageChange={handlePageClick}
                      containerClassName="flex items-center gap-2"
                      pageClassName="hidden sm:block"
                      pageLinkClassName="px-4 py-2 rounded-xl border-2 border-gray-200 hover:border-blue hover:bg-blue/5 transition-all duration-200 font-medium text-grey hover:text-blue"
                      previousClassName="px-4 py-2 rounded-xl border-2 border-gray-200 hover:border-blue hover:bg-blue/5 transition-all duration-200 font-medium text-grey hover:text-blue"
                      nextClassName="px-4 py-2 rounded-xl border-2 border-gray-200 hover:border-blue hover:bg-blue/5 transition-all duration-200 font-medium text-grey hover:text-blue"
                      activeLinkClassName="!bg-blue !text-white !border-blue"
                      disabledClassName="opacity-50 cursor-not-allowed"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-blue px-8 py-6 rounded-t-3xl">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <i className={`fas ${isEditMode ? 'fa-edit' : 'fa-plus'} text-white text-xl`}></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-2xl">
                    {isEditMode ? 'Edit Department' : 'Create New Department'}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {isEditMode ? 'Update department information' : 'Add a new department to the system'}
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={isEditMode ? handleUpdate : handleCreate} className="p-8">
              <div className="space-y-6">
                <div>
                  <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                    <i className="fas fa-building text-blue text-xs"></i>
                    Department Name *
                  </label>
                  <input
                    type="text"
                    value={selectedDepartment.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300"
                    placeholder="e.g., Accounting, Terumo, URC"
                    required
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                    <i className="fas fa-tag text-blue text-xs"></i>
                    Type *
                  </label>
                  <div className="relative">
                    <select
                      value={selectedDepartment.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 appearance-none"
                    >
                      <option value="internal">Internal Department</option>
                      <option value="corporate">Corporate Client</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <i className="fas fa-chevron-down text-blue text-sm"></i>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-code text-blue text-xs"></i>
                      Prefix
                    </label>
                    <input
                      type="text"
                      value={selectedDepartment.prefix}
                      onChange={(e) => handleInputChange('prefix', e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300"
                      placeholder="e.g., accounting, terumo"
                    />
                    <p className="text-xs text-grey mt-2">Used for username generation</p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-hashtag text-blue text-xs"></i>
                      Short Prefix
                    </label>
                    <input
                      type="text"
                      value={selectedDepartment.short_prefix}
                      onChange={(e) => handleInputChange('short_prefix', e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="e.g., ac, cr"
                      maxLength={5}
                      disabled={selectedDepartment.type === 'corporate'}
                    />
                    <p className="text-xs text-grey mt-2">
                      {selectedDepartment.type === 'corporate'
                        ? 'Always "cr" for corporate clients'
                        : 'Short code (2-5 characters)'}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                    <i className="fas fa-image text-blue text-xs"></i>
                    Logo URL
                  </label>
                  <input
                    type="text"
                    value={selectedDepartment.logo_url}
                    onChange={(e) => handleInputChange('logo_url', e.target.value)}
                    className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300"
                    placeholder="https://example.com/logo.png"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                    <i className="fas fa-align-left text-blue text-xs"></i>
                    Description
                  </label>
                  <textarea
                    value={selectedDepartment.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300"
                    rows={3}
                    placeholder="Optional description"
                  />
                </div>

                {isEditMode && (
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-toggle-on text-blue text-xs"></i>
                      Status
                    </label>
                    <div className="relative">
                      <select
                        value={selectedDepartment.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 appearance-none"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  type="submit"
                  className="flex-1 bg-blue hover:bg-blue/80 text-white px-6 py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                >
                  {isEditMode ? 'Update Department' : 'Create Department'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-grey px-6 py-4 rounded-2xl font-semibold transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default DepartmentManagement;
